import { useState } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  AreaChart,
  Area,
  Radar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
} from 'recharts';
import { Zap, TrendingDown, Activity } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { cn } from '../../utils';
import {
  MMEMParams,
  DecayDataPoint,
  SawtoothDataPoint,
  TriadRadarDataPoint,
} from '../../types';
import StatCard from '../StatCard';
import NeuralNetwork from '../NeuralNetwork';

interface MemoryDynamicsProps {
  isDark: boolean;
  L: number;
  params: MMEMParams;
  decayData: DecayDataPoint[];
  sawtoothData: SawtoothDataPoint[];
  triadRadarData: TriadRadarDataPoint[];
}

export default function MemoryDynamics({
  isDark,
  L,
  params,
  decayData,
  sawtoothData,
  triadRadarData,
}: MemoryDynamicsProps) {
  const [activeTab, setActiveTab] = useState<'decay' | 'sawtooth' | 'synapse'>(
    'decay'
  );

  return (
    <motion.section
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay: 0.2 }}
      className="bg-surface/50 border-border-main flex min-h-[650px] flex-col rounded-[2.5rem] border p-10 shadow-2xl shadow-slate-200/50 backdrop-blur-sm dark:shadow-none"
    >
      <div className="mb-10 flex flex-col justify-between gap-6 md:flex-row md:items-center">
        <div>
          <h2 className="text-text-main mb-2 text-3xl font-black tracking-tight">
            Memory Dynamics
          </h2>
          <p className="font-medium text-slate-500 dark:text-slate-400">
            Predictive modeling of information retention.
          </p>
        </div>
        <div className="flex rounded-2xl bg-slate-100/50 p-1.5 backdrop-blur-md dark:bg-slate-800/50">
          <button
            onClick={() => setActiveTab('decay')}
            className={cn(
              'rounded-xl px-6 py-2.5 text-xs font-extrabold transition-all',
              activeTab === 'decay'
                ? 'bg-surface text-primary shadow-md'
                : 'text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300'
            )}
          >
            Decay Curve
          </button>
          <button
            onClick={() => setActiveTab('sawtooth')}
            className={cn(
              'rounded-xl px-6 py-2.5 text-xs font-extrabold transition-all',
              activeTab === 'sawtooth'
                ? 'bg-surface text-primary shadow-md'
                : 'text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300'
            )}
          >
            Sawtooth Effect
          </button>
          <button
            onClick={() => setActiveTab('synapse')}
            className={cn(
              'rounded-xl px-6 py-2.5 text-xs font-extrabold transition-all',
              activeTab === 'synapse'
                ? 'bg-surface text-primary shadow-md'
                : 'text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300'
            )}
          >
            Synaptic Strength
          </button>
        </div>
      </div>
      <div className="relative mt-4 grid min-h-[400px] w-full flex-1 grid-cols-1 gap-8 xl:grid-cols-3">
        <div className="flex items-center justify-center xl:col-span-2">
          <AnimatePresence mode="wait">
            {activeTab === 'decay' ? (
              <motion.div
                key="decay"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="h-full w-full"
              >
                <ResponsiveContainer
                  width="100%"
                  height={400}
                  key={`decay-${isDark}`}
                >
                  <AreaChart
                    data={decayData}
                    margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                  >
                    <defs>
                      <linearGradient
                        id="colorRecall"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop
                          offset="5%"
                          stopColor="#6366f1"
                          stopOpacity={0.3}
                        />
                        <stop
                          offset="95%"
                          stopColor="#6366f1"
                          stopOpacity={0}
                        />
                      </linearGradient>
                    </defs>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      vertical={false}
                      stroke={isDark ? '#334155' : '#f1f5f9'}
                    />
                    <XAxis
                      dataKey="time"
                      tick={{
                        fontSize: 11,
                        fontWeight: 600,
                        fill: isDark ? '#64748b' : '#94a3b8',
                      }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      domain={[0, 100]}
                      tick={{
                        fontSize: 11,
                        fontWeight: 600,
                        fill: isDark ? '#64748b' : '#94a3b8',
                      }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <Tooltip
                      contentStyle={{
                        borderRadius: '24px',
                        border: 'none',
                        boxShadow: '0 25px 50px -12px rgba(0,0,0,0.15)',
                        padding: '16px',
                        backgroundColor: isDark ? '#1e293b' : '#ffffff',
                        color: isDark ? '#f8fafc' : '#0f172a',
                      }}
                      labelStyle={{
                        fontWeight: 800,
                        marginBottom: '4px',
                        color: isDark ? '#f8fafc' : '#0f172a',
                      }}
                      itemStyle={{ fontWeight: 600, color: '#6366f1' }}
                    />
                    <Area
                      type="monotone"
                      dataKey="recall"
                      stroke="#6366f1"
                      strokeWidth={4}
                      fillOpacity={1}
                      fill="url(#colorRecall)"
                      animationDuration={1500}
                    />
                    <ReferenceLine
                      y={30}
                      stroke="#f43f5e"
                      strokeDasharray="8 8"
                      strokeWidth={2}
                      label={{
                        value: 'Recall Threshold',
                        position: 'right',
                        fill: '#f43f5e',
                        fontSize: 11,
                        fontWeight: 800,
                      }}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </motion.div>
            ) : activeTab === 'sawtooth' ? (
              <motion.div
                key="sawtooth"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="h-full w-full"
              >
                <ResponsiveContainer
                  width="100%"
                  height={400}
                  key={`sawtooth-${isDark}`}
                >
                  <LineChart
                    data={sawtoothData}
                    margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      vertical={false}
                      stroke={isDark ? '#334155' : '#f1f5f9'}
                    />
                    <XAxis
                      dataKey="time"
                      tick={{
                        fontSize: 11,
                        fontWeight: 600,
                        fill: isDark ? '#64748b' : '#94a3b8',
                      }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      tick={{
                        fontSize: 11,
                        fontWeight: 600,
                        fill: isDark ? '#64748b' : '#94a3b8',
                      }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <Tooltip
                      contentStyle={{
                        borderRadius: '24px',
                        border: 'none',
                        boxShadow: '0 25px 50px -12px rgba(0,0,0,0.15)',
                        padding: '16px',
                        backgroundColor: isDark ? '#1e293b' : '#ffffff',
                        color: isDark ? '#f8fafc' : '#0f172a',
                      }}
                      labelStyle={{
                        fontWeight: 800,
                        marginBottom: '4px',
                        color: isDark ? '#f8fafc' : '#0f172a',
                      }}
                      itemStyle={{ fontWeight: 600, color: '#6366f1' }}
                    />
                    <Line
                      type="monotone"
                      dataKey="strength"
                      stroke="#6366f1"
                      strokeWidth={4}
                      dot={false}
                      animationDuration={1500}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </motion.div>
            ) : (
              <motion.div
                key="synapse"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                className="flex h-full w-full flex-col items-center justify-center py-6"
              >
                <NeuralNetwork learningValue={L} isDark={isDark} />
                <div className="mt-6 text-center">
                  <div className="text-text-main text-4xl font-black tabular-nums">
                    {Math.round((L / 1200) * 100)}%
                  </div>
                  <div className="mt-2 text-xs font-bold tracking-widest text-slate-400 uppercase">
                    Network Efficiency
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <div className="border-border-main hidden flex-col items-center justify-center border-l pl-8 xl:flex">
          <h3 className="mb-6 text-[10px] font-black tracking-[0.2em] text-slate-400 uppercase">
            Triad Balance
          </h3>
          <ResponsiveContainer width="100%" height={250}>
            <RadarChart
              cx="50%"
              cy="50%"
              outerRadius="80%"
              data={triadRadarData}
            >
              <PolarGrid stroke={isDark ? '#334155' : '#e2e8f0'} />
              <PolarAngleAxis
                dataKey="subject"
                tick={{
                  fontSize: 10,
                  fontWeight: 700,
                  fill: isDark ? '#94a3b8' : '#64748b',
                }}
              />
              <Radar
                name="Triad"
                dataKey="A"
                stroke="#6366f1"
                fill="#6366f1"
                fillOpacity={0.5}
              />
            </RadarChart>
          </ResponsiveContainer>
          <div className="mt-4 flex gap-4">
            <div className="flex items-center gap-1.5">
              <div className="bg-primary h-2 w-2 rounded-full" />
              <span className="text-[10px] font-bold text-slate-500 uppercase">
                Current
              </span>
            </div>
            <div className="flex items-center gap-1.5 opacity-30">
              <div className="h-2 w-2 rounded-full bg-slate-300" />
              <span className="text-[10px] font-bold text-slate-500 uppercase">
                Ideal
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-3">
        <StatCard
          label="Stability Index"
          value={L.toLocaleString()}
          icon={<Activity className="h-5 w-5" />}
          color="primary"
        />
        <StatCard
          label="Retention Half-Life"
          value={`${Math.round(L * Math.log(2) * 10) / 10}u`}
          icon={<TrendingDown className="h-5 w-5" />}
          color="secondary"
        />
        <StatCard
          label="Encoding Power"
          value={`${Math.round(
            ((params.attention * params.mood * params.environment) / 1000) * 100
          )}%`}
          icon={<Zap className="h-5 w-5" />}
          color="accent"
        />
      </div>
    </motion.section>
  );
}
